import React, { useEffect, useState } from "react";
import { statistic2 } from "services/statistic/statistic2";
import styles from "./statistic.module.css";
import commonStyles from "components/commonStyle/commonStyle.module.css";
import StatisticFormat from "components/statisticFormat/statisticFormat";
import { downloadFile, generateCSVContent, months } from "utils/statistic";
import { FacultyData, YearlyData } from "modules/statistic";
import Popup from "components/Popup/popup";
import ErrorFailed from "components/errorFailed/errorFailed";


export default function Statistic2() {
  const [facultyData, setFacultyData] = useState<FacultyData>({});
  const [yearlyData, setYearlyData] = useState<YearlyData>({});
  const [error, setError] = useState<string | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minYear, setMinYear] = useState<number | null>(null);
  const [maxYear, setMaxYear] = useState<number | null>(null);
  const fetchData = async () => {
    try {
      const res = await statistic2(startDate, endDate);
      setFacultyData(res.faculty_data);
      setYearlyData(res.yearly_data);
      const years = Object.keys(res.yearly_data).map(Number);

      const maxYear = Math.max(...years);
      const minYear = Math.min(...years);

      setMaxYear(maxYear);
      setMinYear(minYear);
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
    }
  };

  const handleFilter = () => {
    if (!startDate || !endDate) {
      setError("Bitte geben Sie sowohl ein Startdatum als auch ein Enddatum ein.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError("Das Startdatum kann nicht nach dem Enddatum liegen.");
      return;
    }

    setIsPopupOpen(false);
    setError(null);
    setIsPopupOpen(false);
    fetchData();
  };

  const handleDeleteFilter =  () => {
     setStartDate('');
     setEndDate('');
    setError(null)
    setIsPopupOpen(false);
  };

  const downloadCSV = () => {
    const headers = [
      "Kategorie",
      "Monat",
      ...months,
      "Gesamt",
      "Kundengruppen anteil"
    ];

    const rows: (string | number)[][] = [];



    Object.entries(yearlyData).forEach(([year, types]) => {
      Object.entries(types).forEach(([type, data]) => {
        const row = [
          type,
          year,
          ...months.map((_, monthIndex) =>
            (data as { months: { [key: number]: number } }).months[monthIndex + 1] || 0
          ),
          (data as { total: number }).total,
          (data as { percentage: number }).percentage.toFixed(2) + "%"
        ];
        rows.push(row);
      });
    });

    Object.entries(facultyData).forEach(([faculty, years]) => {
      Object.entries(years).forEach(([year, data]) => {
        const row = [
          faculty,
          year,
          ...months.map((_, monthIndex) => data.months[monthIndex + 1] || 0),
          data.total,
          data.percentage + "%"
        ];
        rows.push(row);
      });
    });

    const csvContent = generateCSVContent(headers, rows);
    downloadFile(csvContent, "Anlage1b.csv");
  };

  useEffect(() => {
    fetchData();
  }, [isPopupOpen]);

  return (
    <>
      <StatisticFormat
        label="Anlage 1b"
        description={startDate && endDate
          ? `Belegungszahlen der "Flexi" im Zeitraum vom ${startDate} bis ${endDate} Anzahl der monatlichen Buchungen (Montag bis Freitag) nach Fakultäten.`
          : `Belegungszahlen der "Flexi" im Zeitraum vom 01.01.${minYear} bis 31.12.${maxYear} Anzahl der monatlichen Buchungen (Montag bis Freitag) nach Fakultäten.`
        } onButton1Click={downloadCSV}
        onFilterButton1Click={() => setIsPopupOpen(true)}
      >
        <div className={styles.container}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Kategorie</th>
                <th>Monat</th>
                {months.map((month) => (
                  <th key={month}>{month}</th>
                ))}
                <th className={styles.gesamt}>Gesamt</th>
                <th className={styles.kundengruppenanteil}>Kundengruppen anteil</th>
              </tr>
            </thead>
            <tbody>
              <tr className={styles.emptyRow}>
                {Array.from({ length: 16 }).map((_, index) => (
                  <td key={`empty-cell-${index}`}></td>
                ))}
              </tr>
              {Object.entries(yearlyData)
                .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                .map(([year, types]) => {
                  const currentYear = Math.max(...Object.keys(yearlyData).map(Number));
                  return (
                    <React.Fragment key={year}>
                      {Object.entries(types).map(([type, data], typeIndex) => (
                        <React.Fragment key={`${year}-${type}`}>
                          <tr className={styles[`year-${(currentYear - parseInt(year)) % 9 + 1}`]}>
                            <td>{type}</td>
                            <td>{year}</td>
                            {months.map((month, monthIndex) => (
                              <td key={monthIndex}>
                                {(data as { months: { [key: number]: number } }).months[monthIndex + 1] || 0}
                              </td>
                            ))}
                            <td>{(data as { total: number }).total}</td>
                            <td>{(data as { percentage: number }).percentage.toFixed(2)}%</td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  );
                })}

              <tr className={styles.emptyRow}>
                {Array.from({ length: 16 }).map((_, index) => (
                  <td key={`empty-cell-${index}`}></td>
                ))}
              </tr>
              {Object.entries(facultyData).map(([faculty, years], facultyIndex) => (
                <React.Fragment key={faculty}>
                  {Object.entries(years)
                    .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                    .map(([year, data], yearIndex) => {
                      const currentYear = Math.max(...Object.keys(years).map(Number));
                      return (
                        <React.Fragment key={`${faculty}-${year}`}>
                          <tr className={styles[`year-${(currentYear - parseInt(year)) % 9 + 1}`]}>
                            <td>{yearIndex === 0 ? faculty : ''}</td>
                            <td>{year}</td>
                            {months.map((month, monthIndex) => (
                              <td key={monthIndex}>
                                {data.months[monthIndex + 1] || 0}
                              </td>
                            ))}
                            <td>{data.total}</td>
                            <td>{data.percentage.toFixed(2)}%</td>
                          </tr>
                        </React.Fragment>
                      );
                    })}

                  <tr className={styles.emptyRow}>
                    {Array.from({ length: 16 }).map((_, index) => (
                      <td key={`empty-cell-${index}`}></td>
                    ))}
                  </tr>
                </React.Fragment>
              ))}

            </tbody>
          </table>
        </div>
      </StatisticFormat>
      <Popup
        title={'Filter'}
        submitLabel={"Einsetzen"}
        closeLabel={"Zurücksetzen"}
        isWithButton={true}
        isOpen={isPopupOpen}
        onClose={handleDeleteFilter} onSubmit={handleFilter}>
        {error && <ErrorFailed error={error} />}

        <div className={commonStyles.formGroupRow}>
          <div className={commonStyles.formGroup}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className={commonStyles.formGroup}>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </Popup></>
  );
}
